import { ATOMS, CODE_SPAN, EMPHASIS_RULES, FENCE_LINE } from '../../messageTypes/richText/parseRichText';
import { getCaretOffset, setCaretOffset } from './composerCaret';
import styles from './decorateComposer.module.scss';

const FMT_ATTR = 'data-dz-fmt';
const LINE_BLOCKS = ['DIV', 'P'];
const LINE_MARKER = /^(\s*)(>|[-*]|\d+\.)(\s)/;
const MENTION_PLACEHOLDER_GROUP = 5;
// Lower wins when two runs start together: a link keeps its underscores literal.
const PRIORITY = { atom: 0, code: 1, emphasis: 2 };
const EMPHASIS_CLASS = { bold: 'em', italic: 'em', strike: 'strike' };
const GLOBAL_EMPHASIS = EMPHASIS_RULES.map(rule => ({
    cls: EMPHASIS_CLASS[rule.type],
    pattern: new RegExp(rule.pattern.source, 'g'),
}));

/**
 * Colours formatting markers, links, emails, phone numbers and @all in the composer while
 * typing (FR103). Only colour and background change, so the caret never drifts; the text and
 * the mention chips stay as typed. Skipped while an input method is composing.
 *
 * @param {HTMLElement} root The composer's contenteditable element.
 * @param {Event} [event] The input event, used to detect an input method in progress.
 * @returns {string} The composer HTML after decorating.
 */
export const decorateComposer = (root, event) => {
    if (!root) return '';
    if (event?.nativeEvent?.isComposing || event?.isComposing) return root.innerHTML;
    const draft = root.cloneNode(true);
    stripDecoration(draft);
    decorateTextNodes(draft);
    if (draft.innerHTML === root.innerHTML) return root.innerHTML;

    const caret = getCaretOffset(root);
    root.replaceChildren(...draft.childNodes);
    if (caret !== null) setCaretOffset(root, caret);
    return root.innerHTML;
};

const stripDecoration = root => {
    root.querySelectorAll(`[${FMT_ATTR}]`).forEach(span => span.replaceWith(...span.childNodes));
    root.normalize();
};

const decorateTextNodes = root => {
    const textNodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: node =>
            node.parentElement?.closest('[contenteditable="false"]')
                ? NodeFilter.FILTER_REJECT
                : NodeFilter.FILTER_ACCEPT,
    });
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach(node => {
        const runs = findRuns(node.nodeValue, startsLine(node, root));
        if (runs.length) node.replaceWith(buildFragment(node.nodeValue, runs));
    });
};

const startsLine = (node, root) => {
    const previous = node.previousSibling;
    if (!previous) return node.parentNode === root || LINE_BLOCKS.includes(node.parentNode?.nodeName);
    if (previous.nodeName === 'BR') return true;
    return previous.nodeType === Node.TEXT_NODE && previous.nodeValue.endsWith('\n');
};

const findRuns = (text, isLineStart) => {
    const candidates = [];
    let lineStart = 0;
    text.split('\n').forEach((line, index) => {
        if (index > 0 || isLineStart) candidates.push(...lineRuns(line, lineStart));
        lineStart += line.length + 1;
    });
    candidates.push(...inlineRuns(text));
    candidates.sort((a, b) => a.start - b.start || a.priority - b.priority);

    const runs = [];
    let end = 0;
    candidates.forEach(run => {
        if (run.start >= end && run.end > run.start) {
            runs.push(run);
            ({ end } = run);
        }
    });
    return runs;
};

const lineRuns = (line, offset) => {
    if (FENCE_LINE.test(line)) return [{ start: offset, end: offset + line.length, cls: 'fence', priority: 0 }];
    const marker = line.match(LINE_MARKER);
    if (!marker) return [];
    const start = offset + marker[1].length;
    return [{ start, end: start + marker[2].length, cls: 'mark', priority: 0 }];
};

const inlineRuns = text => {
    const runs = [];
    for (const match of text.matchAll(ATOMS)) {
        if (match[MENTION_PLACEHOLDER_GROUP]) continue;
        const lead = match[0].length - match[0].trimStart().length;
        const cls = match[0].trim().toLowerCase() === '@all' ? 'at' : 'hl';
        runs.push({ start: match.index + lead, end: match.index + match[0].length, cls, priority: PRIORITY.atom });
    }
    for (const match of text.matchAll(CODE_SPAN)) {
        runs.push({ start: match.index, end: match.index + match[0].length, cls: 'code', priority: PRIORITY.code });
    }
    GLOBAL_EMPHASIS.forEach(({ cls, pattern }) => {
        for (const match of text.matchAll(pattern)) {
            runs.push({ start: match.index, end: match.index + match[0].length, cls, priority: PRIORITY.emphasis });
        }
    });
    return runs;
};

const buildFragment = (text, runs) => {
    const fragment = document.createDocumentFragment();
    let cursor = 0;
    runs.forEach(({ start, end, cls }) => {
        if (start > cursor) fragment.append(text.slice(cursor, start));
        const span = document.createElement('span');
        span.setAttribute(FMT_ATTR, cls);
        span.className = styles[cls];
        span.textContent = text.slice(start, end);
        fragment.append(span);
        cursor = end;
    });
    if (cursor < text.length) fragment.append(text.slice(cursor));
    return fragment;
};
